import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type {
  CreateExploreRoundPayload,
  CreateExploreRoundResponse,
} from '@/api/v4/genai-request-history.api';
import { createExploreRound } from '@/api/v4/genai-request-history.api';
import { MutationKey, QueryKey } from '@/config/query-client';
import { notify } from '@/ui/snackbar/notify';

import { getExploreConflictCode } from './build-create-explore-round-payload';
import {
  exploreViewingRoundNumberState,
  isExploreRoundCreatingState,
} from '../explore.state';
import { useSetAtom } from 'jotai';

export const useCreateExploreRoundMutation = (requestId: string) => {
  const { t } = useTranslation('explore');
  const queryClient = useQueryClient();
  const setViewingRoundNumber = useSetAtom(exploreViewingRoundNumberState);
  const setIsCreatingRound = useSetAtom(isExploreRoundCreatingState);

  return useMutation({
    mutationKey: [MutationKey.CreateExploreRound, requestId],
    mutationFn: (payload: CreateExploreRoundPayload) =>
      createExploreRound(requestId, payload),
    onMutate: () => {
      setIsCreatingRound(true);
    },
    onSuccess: async (data: CreateExploreRoundResponse) => {
      queryClient.setQueryData(
        [QueryKey.ExploreRequestHistorySuggestions, requestId],
        data,
      );
      setViewingRoundNumber(data.activeRoundNumber);
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.ExploreHistory],
      });
    },
    onError: (error: unknown) => {
      const code = getExploreConflictCode(error);
      if (code === 'ROUND_CAP_REACHED') {
        notify({ message: t`roundCapReached` });
        return;
      }
      if (code === 'ROUND_TRUNCATE_REQUIRED') {
        return;
      }
      notify({ message: t`roundCreateError` });
    },
    onSettled: () => {
      setIsCreatingRound(false);
    },
  });
};
